// apps/web/src/routes.tsx

import { lazy, Suspense } from 'react';

import { useTranslation } from 'react-i18next';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  isRouteErrorResponse,
  useRouteError,
  useNavigate,
  useParams,
} from 'react-router-dom';

import { AuthGuard, PermissionGuard } from '@alamo/auth';
import { Button, PublicLightLock, Spinner } from '@alamo/ui';

import { CommissionEligibleRoute } from './components/CommissionEligibleRoute';
import { ConsultorOnlyRoute } from './components/ConsultorOnlyRoute';
import { AppLayout } from './layouts/AppLayout';

// ---------------------------------------------------------------------------
// Lazy-loaded pages — public
// ---------------------------------------------------------------------------
const LoginPage = lazy(() => import('./pages/Login/LoginPage'));
const TwoFactorPage = lazy(() => import('./pages/Login/TwoFactorPage'));
const TwoFactorPageLegacy = lazy(() => import('./pages/TwoFactor/TwoFactorPage'));
const RegistrationPage = lazy(() => import('./pages/Registration/RegistrationPage'));
const RegistrationStatusPage = lazy(() => import('./pages/Registration/RegistrationStatusPage'));
const SponsorLandingPage = lazy(() => import('./pages/Cadastro/SponsorLandingPage'));
const FirstAccessPage = lazy(() => import('./pages/FirstAccess/FirstAccessPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPassword/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPassword/ResetPasswordPage'));

// ---------------------------------------------------------------------------
// Lazy-loaded pages — consultor modules
// ---------------------------------------------------------------------------
const DashboardPage = lazy(() => import('./pages/Dashboard/DashboardPage'));
// ESCR-244.3 — Dashboard de gestão comercial (funil, KPIs, cycle time, escopo).
const MetricsPage = lazy(() => import('./pages/Metrics/MetricsPage'));
// ESCR-252.2 — Tela dedicada de Metas (gestão + meta por período c/ prévia).
const GoalsPage = lazy(() => import('./pages/Goals/GoalsPage'));
// Relatórios gerenciais (menu Relatórios — admin/gerente, kpis:read).
// Contrato: docs/contracts/ESCR-relatorios-gerenciais.md.
const AnalystProductivityPage = lazy(() => import('./pages/Reports/AnalystProductivityPage'));
const VehicleRankingPage = lazy(() => import('./pages/Reports/VehicleRankingPage'));
const RegionalConversionPage = lazy(() => import('./pages/Reports/RegionalConversionPage'));
const MonthlyClosingPage = lazy(() => import('./pages/Reports/MonthlyClosingPage'));
// Relatórios Onda 2 — R5 elasticidade · R6 concentração · R7 retenção.
const PriceElasticityPage = lazy(() => import('./pages/Reports/PriceElasticityPage'));
const PortfolioConcentrationPage = lazy(() => import('./pages/Reports/PortfolioConcentrationPage'));
// ESCR-246.3 — Agenda / Lembretes do consultor (Dashboard Fase 3).
const AgendaPage = lazy(() => import('./pages/Agenda/AgendaPage'));
// Phase 01 onboarding-completion (W3.7) — 7-day onboarding challenge
const ChallengePage = lazy(() => import('./pages/Challenge/ChallengePage'));
// Phase 01 onboarding-completion (W3.9) — PF→PJ migration
const MigratePage = lazy(() => import('./pages/Migrate/MigratePage'));
// Phase 01 onboarding-completion (W3.6) — LMS catalog
const TrainingPage = lazy(() => import('./pages/Training/TrainingPage'));
// Phase 01 W3.6-polish — LMS player (native <video> + sidebar)
const TrainingPlayerPage = lazy(() => import('./pages/Training/TrainingPlayerPage'));
// Phase 01 onboarding-completion (W5.3) — admin legal terms CRUD
const AdminLegalTermsPage = lazy(() => import('./pages/AdminLegalTerms/AdminLegalTermsPage'));
// Phase 01 onboarding-completion (W5.2) — admin trainings CRUD
const AdminTrainingsPage = lazy(() => import('./pages/AdminTrainings/AdminTrainingsPage'));
// Phase 01 onboarding-completion (W3.9-polish) — admin migration queue
const AdminMigrationQueuePage = lazy(
  () => import('./pages/AdminMigration/AdminMigrationQueuePage'),
);
// Phase 01 onboarding-completion (W5.4) — admin lead distribution rules
const AdminLeadRulesPage = lazy(() => import('./pages/AdminLeadDistribution/AdminLeadRulesPage'));
// Phase 01 W5.5 — admin global settings (tour toggle)
const TourSettingsPage = lazy(() => import('./pages/AdminSettings/TourSettingsPage'));
// Tela genérica de configurações do sistema (registry-driven) —
// docs/contracts/ESCR-settings-admin-screen.md
const SystemSettingsPage = lazy(() => import('./pages/Admin/SystemSettings/SystemSettingsPage'));
// Admin payment gateway selector (Asaas | Iugu) — docs/contracts/ESCR-iugu-gateway.md
const PaymentGatewaySettingsPage = lazy(
  () => import('./pages/AdminSettings/PaymentGatewaySettingsPage'),
);
// Repasse manual da adesão (pending_payouts) — docs/contracts/ESCR-pending-payouts.md
const PendingPayoutsPage = lazy(() => import('./pages/PendingPayouts/PendingPayoutsPage'));
// Phase 01 W5.6 — onboarding analytics dashboard
const OnboardingMetricsPage = lazy(() => import('./pages/AdminDashboard/OnboardingMetricsPage'));
// Phase 02 — global video library admin
const AdminVideoLibraryPage = lazy(() => import('./pages/AdminVideoLibrary/AdminVideoLibraryPage'));
const QuotesPage = lazy(() => import('./pages/Quotes/QuotesKanbanPage'));
const NewQuotePage = lazy(() => import('./pages/Quotes/NewQuotePage'));
const EditQuotePage = lazy(() => import('./pages/Quotes/EditQuotePage'));
const QuoteDetailPage = lazy(() => import('./pages/Quotes/QuoteDetailPage'));
// Phase 09 Plan 12 (D-23) — nova página detalhe consultor mobile-first 7 tabs.
// Rotas em inglês (convenção): /quotations/:id (pre-aceite) e /proposals/:id
// (pos-aceite). Mesma component detecta tipo via API.
const ProposalDetailPage = lazy(() => import('./pages/Quotes/ProposalDetailPage'));
// ESCR-247.3 — Índice consultor-facing de propostas (Pipeline por etapa + badge
// "Parado +7d"). Distinto do detalhe `/proposals/:id` e da fila do analista.
const ProposalsPipelinePage = lazy(() => import('./pages/Proposals/ProposalsPipelinePage'));
const PortfolioPage = lazy(() => import('./pages/Portfolio/PortfolioPage'));
// Aniversariantes de ASSOCIADO da carteira (consultor). Destino do deep-link do
// card agregado do sininho (?scope=today). Contrato: ESCR-aniversariantes-associados.
const AssociateBirthdaysPage = lazy(() => import('./pages/Associates/AssociateBirthdaysPage'));
// Endosso — tela /endossos (busca CPF → apólices ativas → trocar plano via amend).
const EndossosPage = lazy(() => import('./pages/Endossos/EndossosPage'));
const TeamListPage = lazy(() => import('./pages/Team/TeamListPage'));
const TeamReferralPage = lazy(() => import('./pages/Team/TeamReferralPage'));
const RankingPage = lazy(() => import('./pages/Ranking/RankingPage'));
const CommissionsPage = lazy(() => import('./pages/Commissions/CommissionsPage'));
const EcommercePage = lazy(() => import('./pages/Ecommerce/EcommercePage'));
// Phase 34 — Loja Interna MVP (Plans 34-06): jornada de compra consultor.
const ProductDetailPage = lazy(() => import('./pages/Ecommerce/ProductDetailPage'));
const CartPage = lazy(() => import('./pages/Ecommerce/CartPage'));
const ConfirmOrderPage = lazy(() => import('./pages/Ecommerce/ConfirmOrderPage'));
// ESCR-249.3 — checkout do pedido (PIX/cartão + polling de pagamento).
const StoreOrderPaymentPage = lazy(() => import('./pages/Ecommerce/StoreOrderPaymentPage'));
const OrderSuccessPage = lazy(() => import('./pages/Ecommerce/OrderSuccessPage'));
const MyOrdersPage = lazy(() => import('./pages/Ecommerce/MyOrdersPage'));
const OrderDetailPage = lazy(() => import('./pages/Ecommerce/OrderDetailPage'));
// ESCR-EPIC-26 — Feed de Novidades do consultor (substitui o slot /campaigns).
const AnnouncementsPage = lazy(() => import('./pages/Announcements/AnnouncementsPage'));
const AnnouncementDetailPage = lazy(() => import('./pages/Announcements/AnnouncementDetailPage'));
// TrainingPage lazy import moved up to the Phase 01 W3.6 block (line 42).
const SupportPage = lazy(() => import('./pages/Support/SupportPage'));
// ESCR-271.2 — Tela dedicada dos chamados do consultor (lista paginada,
// newest-first). Registrada antes de /support/tickets/:id para não cair no dinâmico.
const MyTicketsPage = lazy(() => import('./pages/Support/MyTicketsPage'));
// ESCR-271.2 — Central de Chamados: detalhe read-only do chamado do consultor.
// Casa com o deep-link `support_ticket_status_changed` do sininho.
const SupportTicketDetailPage = lazy(() => import('./pages/Support/SupportTicketDetailPage'));
const ProfilePage = lazy(() => import('./pages/Profile/ProfilePage'));
const MyRegistrationPage = lazy(() => import('./pages/MyRegistration/MyRegistrationPage'));
const MeuSitePage = lazy(() => import('./pages/MeuSite/MeuSitePage'));
const PublicQuotationPage = lazy(() => import('./pages/Public/PublicQuotationPage'));
// QR-B — página pública read-only /q/:id (destino do QR do PDF + copiar link +
// WhatsApp). SEM auth. Mobile-first. Contrato: ESCR-qrb-public-quotation-view.
const PublicQuotationReadPage = lazy(() => import('./pages/Public/PublicQuotationReadPage'));
// Phase 09 Plan 13 — Rota pública /p/:token (SEM auth) com 7 variantes
// condicionais por proposal.status. Mobile-first 414px crítico.
const ProposalMagicLinkPage = lazy(() => import('./pages/Public/ProposalMagicLinkPage'));
// Troca de titularidade (WAVE 2) — página pública /transferencia/:token (SEM
// auth, token opaco single-use). O proprietário ANTIGO autoriza/contesta a troca.
// Mobile-first. Contrato: docs/contracts/ESCR-troca-titularidade-wave2.md.
const OwnershipTransferPage = lazy(
  () => import('./pages/Public/OwnershipTransfer/OwnershipTransferPage'),
);
// Phase 10 Plan 07 — Rota pública /p/:token/inspection/* (SEM auth) — PWA
// vistoria flow (intro consent + GPS + captura fotos + selfie + CNH + review
// + face-match polling + liveness condicional). Mobile-first 414px.
const InspectionFlowPage = lazy(() => import('./pages/Public/Inspection/InspectionFlowPage'));
// Phase 11 Plan 10 — PreCompletionHub (D-46).
// Phase 30 Plan 03 — sub-routes dados/documentos/vistoria/pagamento all render
// PreCompletionHubPage (route-driven Sheet derived from URL segment, D-05/R6).
const PreCompletionHubPage = lazy(
  () => import('./pages/Public/PreCompletion/PreCompletionHubPage'),
);
// Phase 18 Plan 09 (REQ-14 / D-14 / D-15 / E.1) — Magic-link cliente filtered
// view de pendencias (items rejected + pending_review) + footer "Enviar para
// nova analise" que dispara recompute + auto-assign round-robin NORMAL.
const PendenciasPage = lazy(() => import('./pages/Public/Pendencias/PendenciasPage'));
// ESCR-magic-link-auth — gate de identidade que embrulha as rotas /p/:token*.
// Named export → adapta para default no dynamic import (React.lazy).
const MagicLinkGate = lazy(() =>
  import('./pages/Public/MagicLink/MagicLinkGate').then((m) => ({ default: m.MagicLinkGate })),
);
const CommunicationPreferencesPage = lazy(
  () => import('./pages/Public/MagicLink/CommunicationPreferencesPage'),
);
// Phase 13 (ESCR-172.5) — Alamo Cobre, lista paginada do consultor.
const MyCompetitorOffersPage = lazy(
  () => import('./pages/CompetitorOffers/MyCompetitorOffersPage'),
);
const CompetitorOfferRequestPage = lazy(
  () => import('./pages/CompetitorOffers/CompetitorOfferRequestPage'),
);

// ---------------------------------------------------------------------------
// Lazy-loaded pages — admin modules
// ---------------------------------------------------------------------------
// Phase 10 Plan 08 — Admin CRUD inspection-types (rota /admin/vistoria/tipos).
const InspectionTypesListPage = lazy(
  () => import('./pages/Admin/Inspection/InspectionTypesListPage'),
);
const InspectionTypeEditPage = lazy(
  () => import('./pages/Admin/Inspection/InspectionTypeEditPage'),
);
// Phase 11 Plan 11 — Admin CRUD document-types / contract-templates / webhooks.
const DocumentTypesListPage = lazy(
  () => import('./pages/Admin/DocumentTypes/DocumentTypesListPage'),
);
// Comissão MLM Genial — admin. Phase 35b: Patamares virou dois níveis —
// lista de TABELAS de comissão + detalhe (grade de faixas da tabela).
const CommissionTablesListPage = lazy(
  () => import('./pages/Admin/Commissions/CommissionTablesListPage'),
);
const CommissionTableDetailPage = lazy(
  () => import('./pages/Admin/Commissions/CommissionTableDetailPage'),
);
const CommissionClosingPage = lazy(() => import('./pages/Admin/Commissions/CommissionClosingPage'));
const CommissionExtractPage = lazy(() => import('./pages/Admin/Commissions/CommissionExtractPage'));
// Ajustes manuais de comissão (crédito/débito) — ESCR-comissao-ajustes-manuais.
const CommissionAdjustmentsPage = lazy(
  () => import('./pages/Admin/Commissions/CommissionAdjustmentsPage'),
);
const ContractTemplatesListPage = lazy(
  () => import('./pages/Admin/ContractTemplates/ContractTemplatesListPage'),
);
const ContractTemplateEditPage = lazy(
  () => import('./pages/Admin/ContractTemplates/ContractTemplateEditPage'),
);
const ConsultantContractTemplatesPage = lazy(
  () => import('./pages/Admin/ConsultantContractTemplates/ConsultantContractTemplatesPage'),
);
const ConsultantContractTemplateEditPage = lazy(
  () => import('./pages/Admin/ConsultantContractTemplates/ConsultantContractTemplateEditPage'),
);
const WebhooksListPage = lazy(() => import('./pages/Admin/Webhooks/WebhooksListPage'));
const AccountsPage = lazy(() => import('./pages/Accounts/AccountsPage'));
const RegistrationQueuePage = lazy(() => import('./pages/AdminRegistration/RegistrationQueuePage'));
const RegistrationDetailPage = lazy(
  () => import('./pages/AdminRegistration/RegistrationDetailPage'),
);
const NetworkDashboardPage = lazy(() => import('./pages/AdminNetwork/NetworkDashboardPage'));
const NetworkConsultantsPage = lazy(() => import('./pages/AdminNetwork/NetworkConsultantsPage'));
const NetworkConsultantCreatePage = lazy(
  () => import('./pages/AdminNetwork/NetworkConsultantCreatePage'),
);
const NetworkConsultantDetailPage = lazy(
  () => import('./pages/AdminNetwork/NetworkConsultantDetailPage'),
);
// Aniversariantes de consultor (admin_alamo + gerente) — deep-link do sininho.
const ConsultantBirthdaysPage = lazy(() => import('./pages/AdminNetwork/ConsultantBirthdaysPage'));
// Inatividade de consultor (admin_alamo + gerente) — listagem read-only.
const ConsultantInactivityPage = lazy(
  () => import('./pages/AdminNetwork/ConsultantInactivityPage'),
);
const AccountsCreatePage = lazy(() => import('./pages/Accounts/AccountsCreatePage'));
const AccountsDetailPage = lazy(() => import('./pages/Accounts/AccountsDetailPage'));
const AccountsEditPage = lazy(() => import('./pages/Accounts/AccountsEditPage'));
const UsersPage = lazy(() => import('./pages/Users/UsersPage'));
const UsersCreatePage = lazy(() => import('./pages/Users/UsersCreatePage'));
const UsersDetailPage = lazy(() => import('./pages/Users/UsersDetailPage'));
const UsersEditPage = lazy(() => import('./pages/Users/UsersEditPage'));
const RolesPage = lazy(() => import('./pages/Roles/RolesPage'));
const RoleDetailPage = lazy(() => import('./pages/Roles/RoleDetailPage'));
const RoleCreatePage = lazy(() => import('./pages/Roles/RoleCreatePage'));
const PermissionMatrixPage = lazy(() => import('./pages/Roles/PermissionMatrixPage'));
const AuditPage = lazy(() => import('./pages/Audit/AuditPage'));
const SettingsPage = lazy(() => import('./pages/Settings/SettingsPage'));
const PartiesListPage = lazy(() => import('./pages/Parties/PartiesListPage'));
const PartiesCreatePersonPage = lazy(() => import('./pages/Parties/PartiesCreatePersonPage'));
const PartiesCreateOrganizationPage = lazy(
  () => import('./pages/Parties/PartiesCreateOrganizationPage'),
);
const PartiesDetailPage = lazy(() => import('./pages/Parties/PartiesDetailPage'));
const PartiesEditPage = lazy(() => import('./pages/Parties/PartiesEditPage'));
const PartiesImportPage = lazy(() => import('./pages/Parties/PartiesImportPage'));
const VehicleCatalogPage = lazy(() => import('./pages/Admin/VehicleCatalog/VehicleCatalogPage'));
// W8.5 — Sistema de Link Configurável
const PublicLinksListPage = lazy(() => import('./pages/Admin/PublicLinks/PublicLinksListPage'));
const PublicLinkFormPage = lazy(() => import('./pages/Admin/PublicLinks/PublicLinkFormPage'));
// ESCR-185.7 — CRUD admin de campanhas/cupons
const AdminCampaignsListPage = lazy(
  () => import('./pages/Admin/Marketing/Campaigns/AdminCampaignsListPage'),
);
const AdminCampaignFormPage = lazy(
  () => import('./pages/Admin/Marketing/Campaigns/AdminCampaignFormPage'),
);
const AdminCampaignViewPage = lazy(
  () => import('./pages/Admin/Marketing/Campaigns/AdminCampaignViewPage'),
);
// ESCR-269 — CRUD admin de Comunicados (announcements)
const AdminAnnouncementsListPage = lazy(
  () => import('./pages/Admin/Marketing/Announcements/AdminAnnouncementsListPage'),
);
const AdminAnnouncementFormPage = lazy(
  () => import('./pages/Admin/Marketing/Announcements/AdminAnnouncementFormPage'),
);
const AdminAnnouncementViewPage = lazy(
  () => import('./pages/Admin/Marketing/Announcements/AdminAnnouncementViewPage'),
);
// ESCR-EPIC-35 — Admin de Fornecedores externos (rastreamento + assistência).
// Contrato: docs/contracts/ESCR-EPIC-35-suppliers-admin.md.
const SuppliersPage = lazy(() => import('./pages/Suppliers/SuppliersPage'));
const SupplierDetailPage = lazy(() => import('./pages/Suppliers/SupplierDetailPage'));
// ESCR-EPIC-35 Fases 1-2 — dashboard de envios + config de rastreamento (rules
// + elegibilidade). Contratos: docs/contracts/ESCR-EPIC-35-fase1-{delivery,config}.md.
const TrackingDispatchesPage = lazy(() => import('./pages/Suppliers/TrackingDispatchesPage'));
const FulfillmentRulesPage = lazy(() => import('./pages/Suppliers/FulfillmentRulesPage'));
// Central de Suporte / FAQ — CRUD admin (perguntas + categorias).
// Contrato: docs/contracts/ESCR-faq-module.md.
const FaqEntriesListPage = lazy(() => import('./pages/Admin/Faq/FaqEntriesListPage'));
const FaqEntryFormPage = lazy(() => import('./pages/Admin/Faq/FaqEntryFormPage'));
const FaqCategoriesListPage = lazy(() => import('./pages/Admin/Faq/FaqCategoriesListPage'));
// ESCR-271.3 — Central de Chamados: fila do atendente + detalhe/atender.
// Permission `support:manage`. Contrato: docs/contracts/ESCR-271-support-tickets.md.
const AdminSupportTicketsPage = lazy(() => import('./pages/Admin/Support/AdminSupportTicketsPage'));
const AdminSupportTicketDetailPage = lazy(
  () => import('./pages/Admin/Support/AdminSupportTicketDetailPage'),
);
// Pricing Admin Redesign v2 — W4a (SD-10 coordinator, Wave 4)
// Legacy /admin/pricing-tables → 301 redirect to /admin/pricing (see routes below).
const PricingAdminShell = lazy(() => import('./pages/Admin/Pricing/PricingAdminShell'));
const PricingDashboard = lazy(() => import('./pages/Admin/Pricing/PricingDashboard'));
// ESCR-205.1 (F2) — fila central de aprovações de pricing.
const PricingApprovalsPage = lazy(() => import('./pages/Admin/Pricing/Approvals/ApprovalsPage'));
const PricingPilar1Page = lazy(() => import('./pages/Admin/Pricing/Pilar1/PricingPilar1Page'));
const PricingPilar2Page = lazy(() => import('./pages/Admin/Pricing/Pilar2/PricingPilar2Page'));
// F3 ESCR-210.8/210.10/207.13 — telas novas Pilar 2.
const Pilar2ProductDetailPage = lazy(
  () => import('./pages/Admin/Pricing/Pilar2/Pilar2ProductDetailPage'),
);
const Pilar2ManageVariantsPage = lazy(
  () => import('./pages/Admin/Pricing/Pilar2/Pilar2ManageVariantsPage'),
);
// ESCR-207.14 — Pilar2MatrixPage virou tab interna (Pilar2MatrixTab) consumida
// por PricingPilar2Page. A rota /admin/pricing/pilar-2/matriz agora redireciona
// via <Navigate> pra ?tab=matriz preservando bookmarks legados.
const PricingPilar3Page = lazy(() => import('./pages/Admin/Pricing/Pilar3/PricingPilar3Page'));
const PricingPilar4Page = lazy(() => import('./pages/Admin/Pricing/Pilar4/PricingPilar4Page'));
const ProtectedValueRulesPage = lazy(
  () => import('./pages/Admin/Pricing/ProtectedValueRules/ProtectedValueRulesPage'),
);
const QuotationSimulatorPage = lazy(
  () => import('./pages/Admin/Pricing/QuotationSimulator/QuotationSimulatorPage'),
);
const GeographyPage = lazy(() => import('./pages/Admin/Geography/GeographyPage'));

// Geography refactor 2026-04-30 — /admin/zones legacy removido. /admin/geography
// passa a apontar para a nova GeographyPage (Pricing › Geografia).
const CoverageMapPage = lazy(() => import('./pages/Admin/CoverageMap/CoverageMapPage'));
// Phase 02.3 P14 Task 4 — Admin suggestions queue (UI-D-19 protótipo v2)
const SuggestionsPage = lazy(() => import('./pages/Admin/Suggestions/SuggestionsPage'));
const ProvisionalQueuePage = lazy(
  () => import('./pages/Admin/ProvisionalQuotations/ProvisionalQueuePage'),
);
const NotificationTemplatesPage = lazy(
  () => import('./pages/AdminNotifications/NotificationTemplatesPage'),
);
const MessagingOverviewPage = lazy(
  () => import('./pages/AdminNotifications/MessagingOverviewPage'),
);
const NotificationEventsPage = lazy(
  () => import('./pages/AdminNotifications/NotificationEventsPage'),
);
const NotificationCadencePage = lazy(
  () => import('./pages/AdminNotifications/NotificationCadencePage'),
);
const NotificationProvidersPage = lazy(
  () => import('./pages/AdminNotifications/NotificationProvidersPage'),
);
const NotificationDeliveriesPage = lazy(
  () => import('./pages/AdminNotifications/NotificationDeliveriesPage'),
);
const RdMarketingPage = lazy(() => import('./pages/AdminNotifications/RdMarketingPage'));
const AdminMultilevelPage = lazy(() => import('./pages/AdminMultilevel/AdminMultilevelPage'));

// ---------------------------------------------------------------------------
// Phase 12 Plan 12 — 5 admin pages (analyst queue + detail + notification
// settings + KPIs + war-room). Permissions:
//   - AnalystQueue/Detail: proposals:analyze
//   - NotificationSettings: admin_alamo (via notification_settings:manage)
//   - Kpis: kpis:read (admin + gerente)
//   - WarRoom: dashboard:war_room (admin_alamo only — Pedro #11)
// ---------------------------------------------------------------------------
// Phase 12 Plan 16 (Sub-A2) — Admin Hinova sync UI (apólices).
// Permissions backend: policies:admin (read/retry). UI gate `roleName ===
// 'admin_alamo'` esconde retry; backend re-valida 401/403.
const PoliciesListPage = lazy(() => import('./pages/Admin/Policies/PoliciesListPage'));
const PolicyDetailPage = lazy(() => import('./pages/Admin/Policies/PolicyDetailPage'));
// Cotação de FROTA V1 (admin-only) — contrato docs/contracts/ESCR-fleet-quotations.md.
// Permission dedicada `fleet_quotations:manage` (admin_alamo + gerente).
const FleetQuotationsListPage = lazy(
  () => import('./pages/Admin/FleetQuotations/FleetQuotationsListPage'),
);
const NewFleetQuotationPage = lazy(
  () => import('./pages/Admin/FleetQuotations/NewFleetQuotationPage'),
);
const FleetQuotationDetailPage = lazy(
  () => import('./pages/Admin/FleetQuotations/FleetQuotationDetailPage'),
);
const AnalystQueuePage = lazy(() => import('./pages/Admin/AnalystQueue/AnalystQueuePage'));
const AnalysisHistoryPage = lazy(() => import('./pages/Admin/AnalystQueue/AnalysisHistoryPage'));
const AnalystDetailPage = lazy(() => import('./pages/Admin/AnalystQueue/AnalystDetailPage'));
// Troca de titularidade (WAVE 2) — bucket "Aguardando titular" (proposals:analyze).
const OwnershipTransferQueuePage = lazy(
  () => import('./pages/Admin/AnalystQueue/OwnershipTransferQueuePage'),
);
const AnalystTeamListPage = lazy(() => import('./pages/Admin/AnalystTeam/AnalystTeamListPage'));
const KpisDashboardPage = lazy(() => import('./pages/Admin/Kpis/KpisDashboardPage'));
const WarRoomPage = lazy(() => import('./pages/Admin/WarRoom/WarRoomPage'));
// ESCR-172.6 — Alamo Cobre admin (queue + standalone detail).
const CompetitorOffersQueuePage = lazy(
  () => import('./pages/Admin/CompetitorOffers/CompetitorOffersQueuePage'),
);
const CompetitorOfferDetailPage = lazy(
  () => import('./pages/Admin/CompetitorOffers/CompetitorOfferDetailPage'),
);
// Phase 34 Plan 07 — Loja Interna admin back-office (produtos, estoque, pedidos).
const AdminStoreProductsPage = lazy(() => import('./pages/Admin/Store/AdminStoreProductsPage'));
const AdminStoreProductFormPage = lazy(
  () => import('./pages/Admin/Store/AdminStoreProductFormPage'),
);
const AdminStoreStockPage = lazy(() => import('./pages/Admin/Store/AdminStoreStockPage'));
const AdminStoreVouchersPage = lazy(() => import('./pages/Admin/Store/AdminStoreVouchersPage'));
const AdminStoreOrdersPage = lazy(() => import('./pages/Admin/Store/AdminStoreOrdersPage'));
const AdminStoreOrderDetailPage = lazy(
  () => import('./pages/Admin/Store/AdminStoreOrderDetailPage'),
);
const AdminStoreCreditPage = lazy(() => import('./pages/Admin/Store/AdminStoreCreditPage'));
const AdminStoreInstallmentsPage = lazy(
  () => import('./pages/Admin/Store/AdminStoreInstallmentsPage'),
);
const AdminStoreFinancialReportPage = lazy(
  () => import('./pages/Admin/Store/AdminStoreFinancialReportPage'),
);

// ---------------------------------------------------------------------------
// Lazy-loaded pages — security & privacy
// ---------------------------------------------------------------------------
const TwoFactorSetupPage = lazy(() => import('./pages/Security/TwoFactorSetupPage'));
const MyDataPage = lazy(() => import('./pages/Privacy/MyDataPage'));
const ConsentsPage = lazy(() => import('./pages/Privacy/ConsentsPage'));
const DeletionRequestsPage = lazy(() => import('./pages/Privacy/DeletionRequestsPage'));

const NotFoundPage = lazy(() => import('./pages/NotFound/NotFoundPage'));

// ---------------------------------------------------------------------------
// Suspense wrapper
// ---------------------------------------------------------------------------
function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex h-screen items-center justify-center">
          <Spinner size="lg" aria-label="Carregando..." />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// ---------------------------------------------------------------------------
// Public forced-light shell — mounts PublicLightLock (engages the theme core's
// forced-light lock while on this route, releases it on leave) alongside the
// page. Wrap every anonymous, login-less public route the client wants pinned to
// light: MagicLink (/p/...) + public quotation (/quote, /q/:id). The bare page
// (Suspense) still renders; the lock only toggles the `.dark` gate.
// ---------------------------------------------------------------------------
function PublicLightShell({ children }: { children: React.ReactNode }) {
  return (
    <>
      <PublicLightLock />
      <SuspenseWrapper>{children}</SuspenseWrapper>
    </>
  );
}

// ---------------------------------------------------------------------------
// Admin ErrorBoundary — friendly error page for admin routes (B14)
// ---------------------------------------------------------------------------
function AdminErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation('common');
  const navigate = useNavigate();

  const message = isRouteErrorResponse(error)
    ? `${error.status} ${error.statusText}`
    : t('messages.unexpected_error');

  return (
    <div className="flex min-h-[50vh] flex-col items-center justify-center p-6">
      <div className="text-center">
        <div className="mb-4 text-5xl" aria-hidden="true">
          &#9888;&#65039;
        </div>
        <h2 className="mb-2 text-xl font-heading font-semibold text-secondary-950">
          {t('messages.unexpected_error')}
        </h2>
        <p className="mb-6 text-sm text-slate-500">{message}</p>
        <div className="flex gap-3">
          <Button variant="primary" onClick={() => navigate(-1)}>
            {t('actions.voltar')}
          </Button>
          <Button variant="outline" onClick={() => window.location.reload()}>
            {t('actions.atualizar')}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Redirect helper for parameterised PT → EN migration
// Preserves :trackingCode param and any query string when redirecting.
// ---------------------------------------------------------------------------
function RedirectToRegistrationStatus() {
  const { trackingCode } = useParams<{ trackingCode: string }>();
  return <Navigate to={`/registration/status/${trackingCode ?? ''}`} replace />;
}

// ---------------------------------------------------------------------------
// Phase 02.3 P13 — Legacy redirect /cotacao/:slug → /quote/:slug (D-15)
// ---------------------------------------------------------------------------
function LegacyCotacaoRedirect() {
  const { slug } = useParams<{ slug: string }>();
  return <Navigate to={`/quote/${slug ?? ''}`} replace />;
}

// ---------------------------------------------------------------------------
// Assinatura: /p/:token/precompletion/assinatura → /p/:token.
// O hub PreCompletion não tem sheet de assinatura — o fluxo oficial vive no
// SignatureVariant renderizado por ProposalMagicLinkPage em /p/:token. Redireciona
// links antigos (e o CTA do hub) para o fluxo correto em vez de um no-op.
// ---------------------------------------------------------------------------
function RedirectPrecompletionAssinatura() {
  const { token } = useParams<{ token: string }>();
  return <Navigate to={`/p/${token ?? ''}`} replace />;
}

// ---------------------------------------------------------------------------
// Page wrapper — PermissionGuard + Suspense (AuthGuard is on the layout)
// ---------------------------------------------------------------------------
function Page({
  resource,
  action,
  children,
}: {
  resource: string;
  action: string;
  children: React.ReactNode;
}) {
  return (
    <PermissionGuard resource={resource} action={action}>
      <SuspenseWrapper>{children}</SuspenseWrapper>
    </PermissionGuard>
  );
}

// ---------------------------------------------------------------------------
// Router — portal único, RBAC controla visibilidade
// ---------------------------------------------------------------------------
export const router = createBrowserRouter([
  // Rotas públicas
  {
    path: '/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/2fa',
    element: (
      <SuspenseWrapper>
        <TwoFactorPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/2fa',
    element: (
      <SuspenseWrapper>
        <TwoFactorPageLegacy />
      </SuspenseWrapper>
    ),
  },

  // Public registration routes
  {
    path: '/registration',
    element: (
      <SuspenseWrapper>
        <RegistrationPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/registration/status/:trackingCode',
    element: (
      <SuspenseWrapper>
        <RegistrationStatusPage />
      </SuspenseWrapper>
    ),
  },

  // Public self-registration link (Carta §4.2): looks up the sponsor by
  // referralCode and forwards to /registration with a read-only indicador.
  {
    path: '/cadastro/:referralCode',
    element: (
      <SuspenseWrapper>
        <SponsorLandingPage />
      </SuspenseWrapper>
    ),
  },

  // Phase 02.3 P13 — Public landing /quote/:slug (canonical en-US per D-15)
  {
    path: '/quote/:slug',
    element: (
      <PublicLightShell>
        <PublicQuotationPage />
      </PublicLightShell>
    ),
  },
  // Default landing without consultor (lead direto Alamo)
  {
    path: '/quote',
    element: (
      <PublicLightShell>
        <PublicQuotationPage />
      </PublicLightShell>
    ),
  },
  // Legacy redirect /cotacao/:slug → /quote/:slug (D-15)
  { path: '/cotacao/:slug', element: <LegacyCotacaoRedirect /> },
  { path: '/cotacao', element: <Navigate to="/quote" replace /> },

  // QR-B — view pública read-only da cotação (/q/:id). SEM auth guard. Destino
  // do QR code do PDF + "copiar link" + WhatsApp. GET /public-quotation/:id
  // (publicApiClient). Mobile-first. Não colide com /quote/:slug nem /p/:token.
  {
    path: '/q/:id',
    element: (
      <PublicLightShell>
        <PublicQuotationReadPage />
      </PublicLightShell>
    ),
  },

  // Troca de titularidade (WAVE 2) — página PÚBLICA do proprietário ANTIGO
  // (/transferencia/:token). SEM auth: o token opaco single-use é a credencial.
  // Mobile-first. Não colide com /q/:id, /quote/:slug nem /p/:token.
  {
    path: '/transferencia/:token',
    element: (
      <PublicLightShell>
        <OwnershipTransferPage />
      </PublicLightShell>
    ),
  },

  // Magic-link público do cliente (FORA do AuthGuard). Sem JWT de consultor.
  // ESCR-magic-link-auth — <MagicLinkGate> embrulha TODA a jornada /p/:token*:
  // exige o desafio de identidade (últimos 4 dígitos do documento + nascimento)
  // e uma sessão de 4h antes de liberar qualquer child. O interceptor do
  // publicApiClient injeta o Bearer nas chamadas /p e remonta o desafio em
  // 410/401. Layout-parent com <Outlet/>: o gate monta uma vez e só o conteúdo
  // da jornada troca ao navegar entre as sub-rotas.
  {
    path: '/p/:token',
    element: (
      <PublicLightShell>
        <MagicLinkGate>
          <Outlet />
        </MagicLinkGate>
      </PublicLightShell>
    ),
    children: [
      // Backend GET /api/v1/p/:token (PublicApiClient). 7 variantes condicionais
      // por proposal.status + 410 Gone para estados terminais (D-09). 99% dos
      // clientes acessam via SMS no celular — mobile-first 414px obrigatório.
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <ProposalMagicLinkPage />
          </SuspenseWrapper>
        ),
      },
      // Sub-rotas Inspection PWA. Nested Routes internos via React Router v6
      // wildcard. PWA, mobile-first, GPS+camera.
      {
        path: 'inspection/*',
        element: (
          <SuspenseWrapper>
            <InspectionFlowPage />
          </SuspenseWrapper>
        ),
      },
      // PreCompletionHub (D-46). Todas as sub-rotas precompletion/* renderizam
      // PreCompletionHubPage; o hub deriva o Sheet aberto do segmento da URL
      // (D-05/R6). Browser back fecha o Sheet sem reload da página.
      {
        path: 'precompletion',
        element: (
          <SuspenseWrapper>
            <PreCompletionHubPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'precompletion/dados',
        element: (
          <SuspenseWrapper>
            <PreCompletionHubPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'precompletion/documentos',
        element: (
          <SuspenseWrapper>
            <PreCompletionHubPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'precompletion/vistoria',
        element: (
          <SuspenseWrapper>
            <PreCompletionHubPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'precompletion/pagamento',
        element: (
          <SuspenseWrapper>
            <PreCompletionHubPage />
          </SuspenseWrapper>
        ),
      },
      // Assinatura → /p/:token (o SignatureVariant vive no root). Redirect fica
      // DENTRO do gate — só um cliente com sessão válida chega a redirecionar.
      {
        path: 'precompletion/assinatura',
        element: <RedirectPrecompletionAssinatura />,
      },
      // Magic-link cliente filtered view pendencias. Cliente abre o link e vê
      // apenas items rejected + pending_review pra refazer; footer dispara POST
      // /resubmit que move proposta para `aguardando_analise` + round-robin
      // auto-assign normal (E.1).
      {
        path: 'pendencias',
        element: (
          <SuspenseWrapper>
            <PendenciasPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'preferencias',
        element: (
          <SuspenseWrapper>
            <CommunicationPreferencesPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // Redirects: PT public registration paths → EN canonical (preserves query string + :trackingCode)
  { path: '/registro', element: <Navigate to="/registration" replace /> },
  { path: '/registro/status/:trackingCode', element: <RedirectToRegistrationStatus /> },

  // Public first access route
  {
    path: '/first-access',
    element: (
      <SuspenseWrapper>
        <FirstAccessPage />
      </SuspenseWrapper>
    ),
  },

  // Public forgot-password route (Plan 02.2.1-01 D-01/D-02) — no auth required.
  // Email-born recovery links shaped https://<host>/forgot-password must render
  // a real page (not the SPA 404). The /login modal stays in place (D-04).
  {
    path: '/forgot-password',
    element: (
      <SuspenseWrapper>
        <ForgotPasswordPage />
      </SuspenseWrapper>
    ),
  },

  // Public reset-password route (Plan 02.1.1.1.1-05b D-10) — requires ?token=...
  {
    path: '/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },

  // -------------------------------------------------------------------------
  // Breadcrumb handles (D-11, Plan 02.1.1.1.1-06)
  // Each protected route below declares `handle.breadcrumb` — either a static
  // pt-BR literal, or a function resolving dynamic labels from params/data.
  // BreadcrumbHost (layouts/components/BreadcrumbHost.tsx) picks these up via
  // useMatches() and renders the trail in the topbar.
  //
  // Note: labels are pt-BR literals (not i18n keys) because RouteObject is
  // defined outside React — no useTranslation hook available. Trade-off
  // intentional: single-locale project (pt-BR primary), breadcrumbs are short
  // navigation labels, and Plan 06 prioritizes shipping the structural fix
  // (D-11 "breadcrumb always shows where you are") over an i18n extraction
  // pass. Follow-up: extract to i18n once BreadcrumbHost resolves via
  // useTranslation inside the component (it already has the hook; could read
  // `breadcrumb_key` instead of `breadcrumb` from handle in a future plan).
  // -------------------------------------------------------------------------

  // Rotas protegidas — AuthGuard + AppLayout (chrome único; consultor em análise
  // opera normalmente e vê o PreApprovalBanner dentro do próprio AppLayout)
  {
    element: (
      <AuthGuard>
        <AppLayout />
      </AuthGuard>
    ),
    children: [
      // Redirect raiz → dashboard
      { path: '/', element: <Navigate to="/dashboard" replace /> },

      // Módulos do consultor
      {
        path: '/dashboard',
        element: (
          <Page resource="dashboard" action="read">
            <DashboardPage />
          </Page>
        ),
        handle: { breadcrumb: 'Painel' },
      },
      {
        // ESCR-244.3 — Dashboard de gestão comercial (funil, KPIs, cycle time).
        path: '/metricas',
        element: (
          <Page resource="dashboard" action="read">
            <MetricsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Métricas' },
      },
      {
        // ESCR-252.2 — Tela dedicada de Metas (gestão + meta por período).
        path: '/metas',
        element: (
          <Page resource="dashboard" action="read">
            <GoalsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Metas' },
      },
      // -----------------------------------------------------------------
      // Relatórios gerenciais (org-wide) — gate kpis:read (admin + gerente).
      // NÃO usar dashboard:read (consultor a tem e os números são da rede
      // inteira). Contrato: docs/contracts/ESCR-relatorios-gerenciais.md.
      // -----------------------------------------------------------------
      {
        path: '/relatorios/produtividade-analistas',
        element: (
          <Page resource="kpis" action="read">
            <AnalystProductivityPage />
          </Page>
        ),
        handle: { breadcrumb: 'Produtividade de analistas' },
      },
      {
        path: '/relatorios/ranking-veiculos',
        element: (
          <Page resource="kpis" action="read">
            <VehicleRankingPage />
          </Page>
        ),
        handle: { breadcrumb: 'Ranking de veículos' },
      },
      {
        path: '/relatorios/conversao-regiao',
        element: (
          <Page resource="kpis" action="read">
            <RegionalConversionPage />
          </Page>
        ),
        handle: { breadcrumb: 'Conversão por região' },
      },
      {
        path: '/relatorios/fechamento-mes',
        element: (
          <Page resource="kpis" action="read">
            <MonthlyClosingPage />
          </Page>
        ),
        handle: { breadcrumb: 'Fechamento de mês' },
      },
      {
        path: '/relatorios/elasticidade-preco',
        element: (
          <Page resource="kpis" action="read">
            <PriceElasticityPage />
          </Page>
        ),
        handle: { breadcrumb: 'Elasticidade de preço' },
      },
      {
        path: '/relatorios/concentracao-carteira',
        element: (
          <Page resource="kpis" action="read">
            <PortfolioConcentrationPage />
          </Page>
        ),
        handle: { breadcrumb: 'Concentração de carteira' },
      },
      {
        // ESCR-246.3 — Agenda / Lembretes do consultor (Dashboard Fase 3).
        // Self-scoped: admin/gerente (sem consultant_profile) caem em 403 no BE;
        // a página exibe aviso "área de consultores".
        path: '/agenda',
        element: (
          <Page resource="dashboard" action="read">
            <AgendaPage />
          </Page>
        ),
        handle: { breadcrumb: 'Agenda' },
      },
      {
        // Phase 01 W3.7 — onboarding challenge (consultor-facing)
        path: '/challenge',
        element: (
          <Page resource="challenge" action="view">
            <ChallengePage />
          </Page>
        ),
        handle: { breadcrumb: 'Desafio 7 dias' },
      },
      {
        // Phase 01 W3.9 — PF→PJ migration UI
        path: '/migrate',
        element: (
          <Page resource="profile" action="update">
            <MigratePage />
          </Page>
        ),
        handle: { breadcrumb: 'Migração PF→PJ' },
      },
      {
        // Phase 01 W3.6 — LMS catalog (consultor-facing list)
        path: '/training',
        element: (
          <Page resource="training" action="view_onboarding">
            <TrainingPage />
          </Page>
        ),
        handle: { breadcrumb: 'Treinamentos' },
      },
      {
        // Phase 01 W3.6-polish — LMS player (native <video> + sidebar)
        path: '/training/:id',
        element: (
          <Page resource="training" action="view_onboarding">
            <TrainingPlayerPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'Treinamentos', href: '/training' },
            { label: 'Treinamento' },
          ],
        },
      },
      {
        // Phase 01 W5.3 — admin legal terms (2FA-protected promote)
        path: '/admin/legal-terms',
        element: (
          <Page resource="permissions" action="manage">
            <AdminLegalTermsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Termos jurídicos' },
      },
      {
        // Phase 01 W5.2 — admin trainings CRUD
        path: '/admin/trainings',
        element: (
          <Page resource="training" action="manage">
            <AdminTrainingsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Treinamentos (admin)' },
      },
      {
        // Phase 01 W3.9-polish — admin PF→PJ migration queue
        path: '/admin/migrations',
        element: (
          <Page resource="admin_registrations" action="update">
            <AdminMigrationQueuePage />
          </Page>
        ),
        handle: { breadcrumb: 'Migrações PF→PJ' },
      },
      {
        // Phase 01 W5.4 — admin lead distribution rules
        path: '/admin/lead-distribution',
        element: (
          <Page resource="admin_network" action="manage">
            <AdminLeadRulesPage />
          </Page>
        ),
        handle: { breadcrumb: 'Distribuição de Leads' },
      },
      {
        // Tela genérica de configurações do sistema (registry-driven).
        // Leitura com settings:read; edição gated na página por settings:manage.
        path: '/admin/settings',
        element: (
          <Page resource="settings" action="read">
            <SystemSettingsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Configurações do Sistema' },
      },
      {
        // Phase 01 W5.5 — admin global tour toggle
        path: '/admin/settings/tour',
        element: (
          <Page resource="settings" action="manage">
            <TourSettingsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Tour de boas-vindas' },
      },
      {
        // Admin payment gateway selector (Asaas | Iugu)
        path: '/admin/settings/payment-gateway',
        element: (
          <Page resource="settings" action="manage">
            <PaymentGatewaySettingsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Gateway de pagamento' },
      },
      {
        // Repasse manual da adesão (pending_payouts) — Iugu único / split gated.
        path: '/admin/pending-payouts',
        element: (
          <Page resource="payouts" action="read">
            <PendingPayoutsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Repasses' },
      },
      {
        // Phase 01 W5.6 — onboarding analytics dashboard
        path: '/admin/dashboard/onboarding',
        element: (
          <Page resource="analytics" action="read">
            <OnboardingMetricsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Métricas de onboarding' },
      },
      {
        // Phase 02 — global video library admin
        path: '/admin/video-library',
        element: (
          <Page resource="video_library" action="read">
            <AdminVideoLibraryPage />
          </Page>
        ),
        handle: { breadcrumb: 'Biblioteca de vídeos' },
      },
      {
        path: '/my-registration',
        element: (
          <Page resource="profile" action="read">
            <MyRegistrationPage />
          </Page>
        ),
        handle: { breadcrumb: 'Meu cadastro' },
      },
      {
        path: '/quotes',
        element: (
          <Page resource="quotation" action="read">
            <QuotesPage />
          </Page>
        ),
        handle: { breadcrumb: 'Cotações' },
      },
      // Alias defensivo — /admin/cotacoes é uma URL intuitiva mas não existe;
      // redireciona pra rota real /quotes em vez de cair no 404.
      { path: '/admin/cotacoes', element: <Navigate to="/quotes" replace /> },
      {
        path: '/quotes/new',
        element: (
          <Page resource="quotation" action="create">
            <NewQuotePage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Cotações', href: '/quotes' }, { label: 'Nova cotação' }],
        },
      },
      {
        path: '/quotes/:id',
        element: (
          <Page resource="quotation" action="read">
            <QuoteDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Cotações', href: '/quotes' }, { label: 'Detalhes' }],
        },
      },
      {
        // ESCR-247.3 — Índice consultor-facing de propostas (Pipeline por etapa
        // + grupos transversais + badge "Parado +7d"). Permission `proposal:read`
        // (= subject RBAC dos endpoints GET /proposals/pipeline + /stalled-count).
        // Distinto de `/proposals/:id` (detalhe, abaixo) e de `/admin/propostas/
        // analise` (fila do analista). Rota estática vem ANTES da dinâmica `:id`.
        path: '/proposals',
        element: (
          <Page resource="proposal" action="read">
            <ProposalsPipelinePage />
          </Page>
        ),
        handle: { breadcrumb: 'Minhas Propostas' },
      },
      // ──────────────────────────────────────────────────────────────────
      // Phase 09 Plan 12 (D-23) — Rotas (em inglês, convenção) para detalhe da
      // cotação/proposta. Mesma component lazy decide layout via API
      // (cotação pré-aceite ou proposta pós-aceite). Permission reusa
      // `quotation:read` — mesmo subject RBAC dos endpoints do backend
      // (@Permissions('quotation:read')). A rota pública /p/:token (sem
      // auth) fica abaixo, fora deste bloco autenticado.
      // ──────────────────────────────────────────────────────────────────
      {
        path: '/quotations/:id',
        element: (
          <Page resource="quotation" action="read">
            <ProposalDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Cotações', href: '/quotes' }, { label: 'Detalhes' }],
        },
      },
      {
        path: '/proposals/:id',
        element: (
          <Page resource="quotation" action="read">
            <ProposalDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Cotações', href: '/quotes' }, { label: 'Detalhes' }],
        },
      },
      // Wave 2.T11 (2026-05-15) — Edição de cotação existente.
      // Reaproveita QuotationWizard via prop initialQuotationId.
      {
        path: '/quotes/:id/edit',
        element: (
          <Page resource="quotation" action="update">
            <EditQuotePage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Cotações', href: '/quotes' }, { label: 'Editar cotação' }],
        },
      },

      // Phase 13 (ESCR-172.5) — Alamo Cobre: lista paginada do consultor.
      // Permission backend: `competitor-offers:request` (consultor_externo + interno).
      {
        path: '/competitor-offers',
        element: (
          <Page resource="competitor-offers" action="request">
            <MyCompetitorOffersPage />
          </Page>
        ),
        handle: { breadcrumb: 'Álamo Cobre' },
      },
      {
        path: '/competitor-offers/:id',
        element: (
          <Page resource="competitor-offers" action="request">
            <CompetitorOfferRequestPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'Álamo Cobre', href: '/competitor-offers' },
            { label: 'Solicitação' },
          ],
        },
      },
      {
        path: '/portfolio',
        element: (
          <Page resource="portfolio" action="read">
            <PortfolioPage />
          </Page>
        ),
        handle: { breadcrumb: 'Portfólio' },
      },
      {
        // Aniversariantes de ASSOCIADO da carteira (consultor). Escopado
        // server-side no voluntário do consultor (portfolio:read). Destino do
        // deep-link do card agregado do sininho (?scope=today).
        path: '/associates/birthdays',
        element: (
          <Page resource="portfolio" action="read">
            <AssociateBirthdaysPage />
          </Page>
        ),
        handle: { breadcrumb: 'Aniversariantes' },
      },
      {
        // Endosso — busca CPF → apólices ativas → trocar plano via amend.
        // Listar (active-by-cpf) exige policies:read (todos os perfis têm).
        // O botão "Endossar" é gateado por policies:amend dentro da própria
        // página (só admin/gerente), então a rota fica em policies:read.
        path: '/endossos',
        element: (
          <Page resource="policies" action="read">
            <EndossosPage />
          </Page>
        ),
        handle: { breadcrumb: 'Endossos' },
      },
      {
        path: '/team',
        element: (
          <ConsultorOnlyRoute>
            <Page resource="team" action="read">
              <TeamListPage />
            </Page>
          </ConsultorOnlyRoute>
        ),
        handle: { breadcrumb: 'Equipe' },
      },
      {
        path: '/team/referrals',
        element: (
          <ConsultorOnlyRoute>
            <Page resource="referrals" action="read">
              <TeamReferralPage />
            </Page>
          </ConsultorOnlyRoute>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Equipe', href: '/team' }, { label: 'Indicações' }],
        },
      },
      {
        path: '/ranking',
        element: (
          <Page resource="ranking" action="read">
            <RankingPage />
          </Page>
        ),
        handle: { breadcrumb: 'Ranking' },
      },
      {
        path: '/commissions',
        element: (
          <CommissionEligibleRoute>
            <Page resource="commissions" action="read">
              <CommissionsPage />
            </Page>
          </CommissionEligibleRoute>
        ),
        handle: { breadcrumb: 'Comissões' },
      },
      {
        path: '/ecommerce',
        element: (
          <Page resource="ecommerce" action="read">
            <EcommercePage />
          </Page>
        ),
        handle: { breadcrumb: 'E-commerce' },
      },
      {
        path: '/ecommerce/products/:id',
        element: (
          <Page resource="ecommerce" action="read">
            <ProductDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'E-commerce', href: '/ecommerce' },
            { label: 'Detalhe do produto' },
          ],
        },
      },
      {
        path: '/ecommerce/cart',
        element: (
          <Page resource="ecommerce" action="read">
            <CartPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'E-commerce', href: '/ecommerce' }, { label: 'Carrinho' }],
        },
      },
      {
        path: '/ecommerce/confirmar',
        element: (
          <Page resource="ecommerce" action="read">
            <ConfirmOrderPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'E-commerce', href: '/ecommerce' },
            { label: 'Confirmar pedido' },
          ],
        },
      },
      {
        path: '/ecommerce/orders',
        element: (
          <Page resource="ecommerce" action="read">
            <MyOrdersPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'E-commerce', href: '/ecommerce' },
            { label: 'Meus pedidos' },
          ],
        },
      },
      {
        path: '/ecommerce/orders/:id',
        element: (
          <Page resource="ecommerce" action="read">
            <OrderDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'E-commerce', href: '/ecommerce' },
            { label: 'Meus pedidos', href: '/ecommerce/orders' },
            { label: 'Detalhe do pedido' },
          ],
        },
      },
      {
        // ESCR-249.3 — checkout do pedido (PIX/cartão à vista + polling).
        path: '/ecommerce/orders/:id/pay',
        element: (
          <Page resource="ecommerce" action="read">
            <StoreOrderPaymentPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'E-commerce', href: '/ecommerce' },
            { label: 'Meus pedidos', href: '/ecommerce/orders' },
            { label: 'Pagamento' },
          ],
        },
      },
      {
        path: '/ecommerce/orders/:id/success',
        element: (
          <Page resource="ecommerce" action="read">
            <OrderSuccessPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'E-commerce', href: '/ecommerce' },
            { label: 'Pedido realizado' },
          ],
        },
      },
      {
        path: '/novidades',
        element: (
          <Page resource="announcements" action="read">
            <AnnouncementsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Novidades' },
      },
      {
        path: '/novidades/:id',
        element: (
          <Page resource="announcements" action="read">
            <AnnouncementDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Novidades', href: '/novidades' }, { label: 'Detalhes' }],
        },
      },
      {
        path: '/training',
        element: (
          <Page resource="training" action="read">
            <TrainingPage />
          </Page>
        ),
        handle: { breadcrumb: 'Treinamentos' },
      },
      {
        path: '/support',
        element: (
          <Page resource="support" action="read">
            <SupportPage />
          </Page>
        ),
        handle: { breadcrumb: 'Suporte' },
      },
      {
        // ESCR-271.2 — Lista dedicada e paginada dos chamados do consultor.
        // Rota estática ANTES da dinâmica /support/tickets/:id.
        path: '/support/tickets',
        element: (
          <Page resource="support" action="read">
            <MyTicketsPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Suporte', href: '/support' }, { label: 'Meus chamados' }],
        },
      },
      {
        // ESCR-271.2 — Detalhe read-only do chamado do consultor. Casa com o
        // deep-link `support_ticket_status_changed` do sininho (/support/tickets/:id).
        path: '/support/tickets/:id',
        element: (
          <Page resource="support" action="read">
            <SupportTicketDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Suporte', href: '/support' }, { label: 'Chamado' }],
        },
      },
      {
        path: '/profile',
        element: (
          <Page resource="profile" action="read">
            <ProfilePage />
          </Page>
        ),
        handle: { breadcrumb: 'Meu perfil' },
      },
      {
        path: '/my-site',
        element: (
          <Page resource="site" action="manage">
            <MeuSitePage />
          </Page>
        ),
        handle: { breadcrumb: 'Meu site' },
      },

      // Segurança (todos os usuários autenticados)
      {
        path: '/security/2fa',
        element: (
          <SuspenseWrapper>
            <TwoFactorSetupPage />
          </SuspenseWrapper>
        ),
        handle: { breadcrumb: 'Segurança' },
      },

      // Privacidade / LGPD (todos os usuários autenticados)
      {
        path: '/my-data',
        element: (
          <SuspenseWrapper>
            <MyDataPage />
          </SuspenseWrapper>
        ),
        handle: { breadcrumb: 'Meus dados' },
      },
      {
        path: '/consents',
        element: (
          <SuspenseWrapper>
            <ConsentsPage />
          </SuspenseWrapper>
        ),
        handle: { breadcrumb: 'Consentimentos' },
      },

      // Redirects: PT authenticated paths → EN canonical (preserves query string)
      { path: '/meu-site', element: <Navigate to="/my-site" replace /> },
      { path: '/seguranca/2fa', element: <Navigate to="/security/2fa" replace /> },
      { path: '/meus-dados', element: <Navigate to="/my-data" replace /> },
      { path: '/consentimentos', element: <Navigate to="/consents" replace /> },
      {
        path: '/admin/privacy/deletion-requests',
        element: (
          <Page resource="privacy" action="manage_deletions">
            <DeletionRequestsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Exclusões de dados' },
      },

      // Módulos administrativos
      {
        path: '/offices',
        element: (
          <Page resource="offices" action="read">
            <AccountsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Escritórios' },
      },
      {
        path: '/offices/new',
        element: (
          <Page resource="offices" action="create">
            <AccountsCreatePage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'Escritórios', href: '/offices' },
            { label: 'Novo escritório' },
          ],
        },
      },
      {
        path: '/offices/:id',
        element: (
          <Page resource="offices" action="read">
            <AccountsDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Escritórios', href: '/offices' }, { label: 'Detalhes' }],
        },
      },
      {
        path: '/offices/:id/edit',
        element: (
          <Page resource="offices" action="update">
            <AccountsEditPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Escritórios', href: '/offices' }, { label: 'Editar' }],
        },
      },
      {
        path: '/users',
        element: (
          <Page resource="users" action="read">
            <UsersPage />
          </Page>
        ),
        handle: { breadcrumb: 'Usuários' },
      },
      {
        path: '/users/new',
        element: (
          <Page resource="users" action="create">
            <UsersCreatePage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Usuários', href: '/users' }, { label: 'Novo usuário' }],
        },
      },
      {
        path: '/users/:id',
        element: (
          <Page resource="users" action="read">
            <UsersDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Usuários', href: '/users' }, { label: 'Detalhes' }],
        },
      },
      {
        path: '/users/:id/edit',
        element: (
          <Page resource="users" action="update">
            <UsersEditPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Usuários', href: '/users' }, { label: 'Editar' }],
        },
      },

      // Roles & Permissions
      {
        path: '/permissions',
        element: (
          <Page resource="permissions" action="read">
            <RolesPage />
          </Page>
        ),
        handle: { breadcrumb: 'Permissões' },
      },
      {
        path: '/permissions/new',
        element: (
          <Page resource="permissions" action="manage">
            <RoleCreatePage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'Permissões', href: '/permissions' },
            { label: 'Novo perfil' },
          ],
        },
      },
      {
        path: '/permissions/:id',
        element: (
          <Page resource="permissions" action="read">
            <RoleDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Permissões', href: '/permissions' }, { label: 'Detalhes' }],
        },
      },
      {
        path: '/permissions/:id/permissions',
        element: (
          <Page resource="permissions" action="manage">
            <PermissionMatrixPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Permissões', href: '/permissions' }, { label: 'Matriz' }],
        },
      },

      // Parties (PF/PJ)
      {
        path: '/parties',
        element: (
          <Page resource="parties" action="read">
            <PartiesListPage />
          </Page>
        ),
        handle: { breadcrumb: 'Partes (PF/PJ)' },
      },
      {
        path: '/parties/new/person',
        element: (
          <Page resource="parties" action="create">
            <PartiesCreatePersonPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'Partes (PF/PJ)', href: '/parties' },
            { label: 'Nova pessoa' },
          ],
        },
      },
      {
        path: '/parties/import',
        element: (
          <Page resource="parties" action="create">
            <PartiesImportPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Partes (PF/PJ)', href: '/parties' }, { label: 'Importar' }],
        },
      },
      {
        path: '/parties/new/organization',
        element: (
          <Page resource="parties" action="create">
            <PartiesCreateOrganizationPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [
            { label: 'Partes (PF/PJ)', href: '/parties' },
            { label: 'Nova organização' },
          ],
        },
      },
      {
        path: '/parties/:id',
        element: (
          <Page resource="parties" action="read">
            <PartiesDetailPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Partes (PF/PJ)', href: '/parties' }, { label: 'Detalhes' }],
        },
      },
      {
        path: '/parties/:id/edit',
        element: (
          <Page resource="parties" action="update">
            <PartiesEditPage />
          </Page>
        ),
        handle: {
          breadcrumb: () => [{ label: 'Partes (PF/PJ)', href: '/parties' }, { label: 'Editar' }],
        },
      },

      {
        path: '/audit',
        element: (
          <Page resource="audit" action="read">
            <AuditPage />
          </Page>
        ),
        handle: { breadcrumb: 'Auditoria' },
      },

      // Admin Registration Approvals
      {
        path: '/admin/registrations',
        element: (
          <Page resource="admin_registrations" action="read">
            <RegistrationQueuePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Fila de aprovação' },
      },
      {
        path: '/admin/registrations/:id',
        element: (
          <Page resource="admin_registrations" action="read">
            <RegistrationDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Fila de aprovação', href: '/admin/registrations' },
            { label: 'Detalhes' },
          ],
        },
      },

      // Admin Network Dashboard
      {
        path: '/admin/network',
        element: (
          <Page resource="admin_network" action="read">
            <NetworkDashboardPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Rede' },
      },
      {
        path: '/admin/network/consultants',
        element: (
          <Page resource="admin_network" action="read">
            <NetworkConsultantsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Consultores' },
      },
      // Cadastro direto (admin) — declarado ANTES de :id para não colidir.
      {
        path: '/admin/network/consultants/new',
        element: (
          <Page resource="admin_network" action="manage">
            <NetworkConsultantCreatePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Consultores', href: '/admin/network/consultants' },
            { label: 'Cadastrar consultor' },
          ],
        },
      },
      // Inatividade de consultores (read-only) — declarado ANTES de :id.
      {
        path: '/admin/network/consultants/inatividade',
        element: (
          <Page resource="admin_network" action="read">
            <ConsultantInactivityPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Consultores', href: '/admin/network/consultants' },
            { label: 'Inatividade' },
          ],
        },
      },
      {
        path: '/admin/network/consultants/:id',
        element: (
          <Page resource="admin_network" action="read">
            <NetworkConsultantDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Consultores', href: '/admin/network/consultants' },
            { label: 'Detalhes' },
          ],
        },
      },

      // Aniversariantes de consultor (admin_alamo + gerente). Destino do
      // deep-link da notificação `consultant_birthday` (?scope=today).
      {
        path: '/admin/consultants/birthdays',
        element: (
          <Page resource="admin_network" action="read">
            <ConsultantBirthdaysPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Aniversariantes' },
      },

      {
        path: '/settings',
        element: (
          <Page resource="settings" action="read">
            <SettingsPage />
          </Page>
        ),
        handle: { breadcrumb: 'Configurações' },
      },

      // Central administrativa de mensageria.
      {
        path: '/admin/notifications',
        element: (
          <Page resource="notification_settings" action="manage">
            <MessagingOverviewPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Notificações' },
      },
      {
        path: '/admin/notifications/events',
        element: (
          <Page resource="notification_settings" action="manage">
            <NotificationEventsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Eventos e canais' },
      },
      {
        path: '/admin/notifications/templates',
        element: (
          <Page resource="notification_templates" action="read">
            <NotificationTemplatesPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Modelos de mensagem' },
      },
      {
        path: '/admin/notifications/cadence',
        element: (
          <Page resource="notification_settings" action="manage">
            <NotificationCadencePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Cadências' },
      },
      {
        path: '/admin/notifications/providers',
        element: (
          <Page resource="notification_settings" action="manage">
            <NotificationProvidersPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Provedores' },
      },
      {
        path: '/admin/notifications/deliveries',
        element: (
          <Page resource="notification_settings" action="manage">
            <NotificationDeliveriesPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Entregas' },
      },
      {
        path: '/admin/notifications/marketing-rd',
        element: (
          <Page resource="marketing_integrations" action="read">
            <RdMarketingPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Marketing RD' },
      },

      // Admin Multilevel — stub (ComingSoonPage per D-09, Phase 02.1.1.1.1-04)
      {
        path: '/admin/multilevel',
        element: (
          <Page resource="team" action="manage_levels">
            <AdminMultilevelPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Multinível' },
      },

      // Admin Vehicle Catalog — Phase 02.3 P05 (ESCR-041.2-5 + ESCR-044)
      {
        path: '/admin/vehicle-catalog',
        element: (
          <Page resource="pricing" action="manage">
            <VehicleCatalogPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Catálogo de veículos' },
      },

      // Phase 10 Plan 08 — Admin CRUD inspection-types
      // Permission backend: `inspection_types:manage` (admin_alamo + gerente).
      {
        path: '/admin/vistoria/tipos',
        element: (
          <Page resource="inspection_types" action="manage">
            <InspectionTypesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Tipos de vistoria' },
      },
      {
        path: '/admin/vistoria/tipos/:id/edit',
        element: (
          <Page resource="inspection_types" action="manage">
            <InspectionTypeEditPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Tipos de vistoria', href: '/admin/vistoria/tipos' },
            { label: 'Editar tipo de vistoria' },
          ],
        },
      },

      // Phase 11 Plan 11 — Admin CRUD document-types
      // Permission backend: `document_types:manage` (admin_alamo + gerente).
      {
        path: '/admin/documentos/tipos',
        element: (
          <Page resource="document_types" action="manage">
            <DocumentTypesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Tipos de documento' },
      },

      // Comissão MLM Genial — admin (commissions:manage; closing usa
      // commissions:process no backend, coberto pelo wildcard :manage).
      {
        path: '/admin/comissoes/patamares',
        element: (
          <Page resource="commissions" action="manage">
            <CommissionTablesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Tabelas de comissão' },
      },
      {
        path: '/admin/comissoes/patamares/:tableId',
        element: (
          <Page resource="commissions" action="manage">
            <CommissionTableDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Tabelas de comissão', href: '/admin/comissoes/patamares' },
            { label: 'Tabela de comissão' },
          ],
        },
      },
      {
        path: '/admin/comissoes/fechamento',
        element: (
          <Page resource="commissions" action="manage">
            <CommissionClosingPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Fechamento de comissão' },
      },
      {
        path: '/admin/comissoes/extrato',
        element: (
          <Page resource="commissions" action="manage">
            <CommissionExtractPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Extrato de comissão' },
      },
      // Ajustes manuais (crédito/débito). Leitura = commissions:manage; as ações
      // de mutação (lançar/cancelar/importar) são gated por commissions:adjust
      // dentro da própria página.
      {
        path: '/admin/comissoes/lancamentos',
        element: (
          <Page resource="commissions" action="manage">
            <CommissionAdjustmentsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Ajustes de comissão' },
      },

      // Phase 11 Plan 11 — Admin CRUD contract-templates
      // Permission backend: `contract_templates:manage`.
      {
        path: '/admin/contratos/templates',
        element: (
          <Page resource="contract_templates" action="manage">
            <ContractTemplatesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Termo de adesão' },
      },
      {
        path: '/admin/contratos/templates/new',
        element: (
          <Page resource="contract_templates" action="manage">
            <ContractTemplateEditPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Termo de adesão', href: '/admin/contratos/templates' },
            { label: 'Novo template' },
          ],
        },
      },
      {
        path: '/admin/contratos/templates-consultor',
        element: (
          <Page resource="contract_templates" action="manage">
            <ConsultantContractTemplatesPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Contrato do consultor' },
      },
      {
        path: '/admin/contratos/templates-consultor/new',
        element: (
          <Page resource="contract_templates" action="manage">
            <ConsultantContractTemplateEditPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Contrato do consultor', href: '/admin/contratos/templates-consultor' },
            { label: 'Novo template de contrato de consultor' },
          ],
        },
      },
      {
        path: '/admin/contratos/templates-consultor/:id',
        element: (
          <Page resource="contract_templates" action="manage">
            <ConsultantContractTemplateEditPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Contrato do consultor', href: '/admin/contratos/templates-consultor' },
            { label: 'Editar template de contrato de consultor' },
          ],
        },
      },
      {
        path: '/admin/contratos/templates/:id',
        element: (
          <Page resource="contract_templates" action="manage">
            <ContractTemplateEditPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Termo de adesão', href: '/admin/contratos/templates' },
            { label: 'Editar template' },
          ],
        },
      },

      // Phase 11 Plan 11 — Admin webhooks dashboard
      // Permission backend: `webhooks:read` + `webhooks:retry`.
      {
        path: '/admin/webhooks',
        element: (
          <Page resource="webhooks" action="read">
            <WebhooksListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Webhooks' },
      },

      // W8.5 — Public Quote Link Configs admin (CRUD)
      {
        path: '/admin/public-links',
        element: (
          <Page resource="pricing" action="manage">
            <PublicLinksListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Links Públicos' },
      },
      {
        path: '/admin/public-links/new',
        element: (
          <Page resource="pricing" action="manage">
            <PublicLinkFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Links Públicos', href: '/admin/public-links' },
            { label: 'Novo link público' },
          ],
        },
      },
      {
        path: '/admin/public-links/:id/edit',
        element: (
          <Page resource="pricing" action="manage">
            <PublicLinkFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Links Públicos', href: '/admin/public-links' },
            { label: 'Editar link público' },
          ],
        },
      },
      // ESCR-185.7 — CRUD admin de campanhas/cupons promocionais.
      // Workflow SD-1 (submit/approve/reject) consumido via lista; nova
      // campanha sempre nasce em status='draft' (backend enforces).
      {
        path: '/admin/marketing/campanhas',
        element: (
          <Page resource="campaigns" action="read">
            <AdminCampaignsListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Campanhas' },
      },
      {
        path: '/admin/marketing/campanhas/new',
        element: (
          <Page resource="campaigns" action="edit">
            <AdminCampaignFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Campanhas', href: '/admin/marketing/campanhas' },
            { label: 'Nova campanha' },
          ],
        },
      },
      {
        path: '/admin/marketing/campanhas/:id/edit',
        element: (
          <Page resource="campaigns" action="edit">
            <AdminCampaignFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Campanhas', href: '/admin/marketing/campanhas' },
            { label: 'Editar campanha' },
          ],
        },
      },
      // Visualização read-only — abre campanhas que o backend bloqueia para
      // edição (published/archived/expired) sem entrar no form de edição.
      {
        path: '/admin/marketing/campanhas/:id/view',
        element: (
          <Page resource="campaigns" action="read">
            <AdminCampaignViewPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Campanhas', href: '/admin/marketing/campanhas' },
            { label: 'Detalhes da campanha' },
          ],
        },
      },

      // ESCR-269 — CRUD admin de Comunicados (announcements). Máquina de estados
      // draft → published → archived (sem approval; D-4). PermissionGuard
      // announcements:manage (decisão RBAC — docs/decisions/ANNOUNCEMENT-RBAC.md).
      {
        path: '/admin/comunicados',
        element: (
          <Page resource="announcements" action="manage">
            <AdminAnnouncementsListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Comunicados' },
      },
      {
        path: '/admin/comunicados/new',
        element: (
          <Page resource="announcements" action="manage">
            <AdminAnnouncementFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Comunicados', href: '/admin/comunicados' },
            { label: 'Novo comunicado' },
          ],
        },
      },
      {
        path: '/admin/comunicados/:id/edit',
        element: (
          <Page resource="announcements" action="manage">
            <AdminAnnouncementFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Comunicados', href: '/admin/comunicados' },
            { label: 'Editar comunicado' },
          ],
        },
      },
      {
        path: '/admin/comunicados/:id',
        element: (
          <Page resource="announcements" action="manage">
            <AdminAnnouncementViewPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Comunicados', href: '/admin/comunicados' },
            { label: 'Detalhes do comunicado' },
          ],
        },
      },

      // ESCR-EPIC-35 (Fase 0) — CRUD admin de Fornecedores externos (rastreamento
      // + assistência). Permission `suppliers:manage`. Contrato:
      // docs/contracts/ESCR-EPIC-35-suppliers-admin.md.
      {
        path: '/admin/suppliers',
        element: (
          <Page resource="suppliers" action="manage">
            <SuppliersPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Fornecedores' },
      },
      {
        path: '/admin/suppliers/:id',
        element: (
          <Page resource="suppliers" action="manage">
            <SupplierDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Fornecedores', href: '/admin/suppliers' },
            { label: 'Detalhes do fornecedor' },
          ],
        },
      },
      // ESCR-EPIC-35 (Fase 1) — Dashboard de envios a fornecedores de rastreamento.
      // Contrato: docs/contracts/ESCR-EPIC-35-fase1-delivery.md.
      {
        path: '/admin/tracking/dispatches',
        element: (
          <Page resource="suppliers" action="manage">
            <TrackingDispatchesPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Envios a Fornecedores' },
      },
      // ESCR-EPIC-35 (Fases 1-2) — Config de rastreamento (produtos que exigem
      // rastreador + elegibilidade produto↔fornecedor).
      // Contrato: docs/contracts/ESCR-EPIC-35-fase1-config.md.
      {
        path: '/admin/fulfillment-rules',
        element: (
          <Page resource="suppliers" action="manage">
            <FulfillmentRulesPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Regras de Rastreamento' },
      },

      // Central de Suporte / FAQ — CRUD admin (perguntas + categorias).
      // Permission `faq:manage`. Contrato: docs/contracts/ESCR-faq-module.md.
      {
        path: '/admin/faq',
        element: (
          <Page resource="faq" action="manage">
            <FaqEntriesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Central de Suporte' },
      },
      {
        path: '/admin/faq/new',
        element: (
          <Page resource="faq" action="manage">
            <FaqEntryFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Central de Suporte', href: '/admin/faq' },
            { label: 'Nova pergunta' },
          ],
        },
      },
      {
        path: '/admin/faq/categories',
        element: (
          <Page resource="faq" action="manage">
            <FaqCategoriesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Central de Suporte', href: '/admin/faq' },
            { label: 'Categorias' },
          ],
        },
      },
      {
        path: '/admin/faq/:id/edit',
        element: (
          <Page resource="faq" action="manage">
            <FaqEntryFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Central de Suporte', href: '/admin/faq' },
            { label: 'Editar pergunta' },
          ],
        },
      },

      // Central de Chamados — fila do atendente (admin). Permission `support:manage`.
      // Contrato: docs/contracts/ESCR-271-support-tickets.md.
      {
        path: '/admin/support',
        element: (
          <Page resource="support" action="manage">
            <AdminSupportTicketsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Chamados' },
      },
      {
        // Destino do deep-link `support_ticket_created` do sininho.
        path: '/admin/support/:id',
        element: (
          <Page resource="support" action="manage">
            <AdminSupportTicketDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [{ label: 'Chamados', href: '/admin/support' }, { label: 'Atender' }],
        },
      },

      // Admin Pricing Tables — Phase 02.3 P06 (ESCR-042.1-4)
      // Legacy route preserved as 301 redirect → /admin/pricing (pricing-admin-v2 W5 removes)
      // Phase 02.3.6 D-08: also redirect any sub-path (/admin/pricing-tables/...)
      // back to /admin/pricing so deep links from email/bookmarks don't 404.
      {
        path: '/admin/pricing-tables',
        element: <Navigate to="/admin/pricing" replace />,
      },
      {
        path: '/admin/pricing-tables/*',
        element: <Navigate to="/admin/pricing" replace />,
      },

      // Pricing Admin Redesign v2 — W4a (SD-10 coordinator)
      // Nested shell: 7 rotas sob /admin/pricing/* (dashboard index + 6 pilares).
      // W4b/W4c preenchem os pilares; W4a scaffold rota + i18n + sidebar accordion.
      {
        path: '/admin/pricing',
        element: (
          <Page resource="pricing" action="manage">
            <PricingAdminShell />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Precificação' },
        children: [
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <PricingDashboard />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'pilar-1-tabela-base',
            element: (
              <SuspenseWrapper>
                <PricingPilar1Page />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Pilar 1 — Tabela Base' },
          },
          {
            path: 'pilar-2-produtos',
            element: (
              <SuspenseWrapper>
                <PricingPilar2Page />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Pilar 2 — Produtos' },
          },
          {
            // F3 ESCR-210.8 — detalhe de produto.
            path: 'pilar-2/produtos/:id',
            element: (
              <SuspenseWrapper>
                <Pilar2ProductDetailPage />
              </SuspenseWrapper>
            ),
            handle: {
              breadcrumb: () => [
                { label: 'Pilar 2 — Produtos', href: '/admin/pricing/pilar-2-produtos' },
                { label: 'Detalhe do produto' },
              ],
            },
          },
          {
            // F3 ESCR-210.10 — gerenciar variantes dedicada.
            path: 'pilar-2/produtos/:id/variantes',
            element: (
              <SuspenseWrapper>
                <Pilar2ManageVariantsPage />
              </SuspenseWrapper>
            ),
            handle: {
              breadcrumb: () => [
                { label: 'Pilar 2 — Produtos', href: '/admin/pricing/pilar-2-produtos' },
                { label: 'Gerenciar variantes' },
              ],
            },
          },
          {
            // ESCR-207.14 — Matriz consolidada como tab interna de Pilar 2.
            // Redirect preserva bookmarks legados de Console B / link sharing.
            path: 'pilar-2/matriz',
            element: <Navigate to="/admin/pricing/pilar-2-produtos?tab=matriz" replace />,
          },
          {
            path: 'pilar-3-ajustes',
            element: (
              <SuspenseWrapper>
                <PricingPilar3Page />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Pilar 3 — Ajustes' },
          },
          {
            path: 'pilar-4-franquias-ativacao',
            element: (
              <SuspenseWrapper>
                <PricingPilar4Page />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Pilar 4 — Franquias + Ativação' },
          },
          {
            // Proteção FIPE % (LMI exibido) — CRUD das regras de percentual.
            path: 'protected-value-rules',
            element: (
              <SuspenseWrapper>
                <ProtectedValueRulesPage />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Proteção FIPE %' },
          },
          {
            path: 'geografia',
            element: (
              <SuspenseWrapper>
                <GeographyPage />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Geografia' },
          },
          {
            path: 'quotation-simulator',
            element: (
              <SuspenseWrapper>
                <QuotationSimulatorPage />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Simulador' },
          },
          // ESCR-205.1 (F2) — fila central de aprovações cross-pilar.
          {
            path: 'approvals',
            element: (
              <SuspenseWrapper>
                <PricingApprovalsPage />
              </SuspenseWrapper>
            ),
            handle: { breadcrumb: 'Aprovações' },
          },
          // Legacy route preserved as 301-style redirect → /admin/pricing/quotation-simulator
          // (Wave 4 refator prototipo Fernando — rename para alinhar com CLAUDE.md
          //  regra "route paths in English"). Telemetria 1 sprint pra confirmar
          //  zero acessos via URL antiga antes de remover.
          {
            path: 'cotacao-simulador',
            element: <Navigate to="/admin/pricing/quotation-simulator" replace />,
          },
          {
            path: 'cotacao-simulador/*',
            element: <Navigate to="/admin/pricing/quotation-simulator" replace />,
          },
        ],
      },

      // /admin/geography legacy → redirect para /admin/pricing/geografia (sub-rota do shell).
      {
        path: '/admin/geography',
        element: <Navigate to="/admin/pricing/geografia" replace />,
      },
      // Phase Geofences W5 — Coverage map dashboard
      {
        path: '/admin/coverage-map',
        element: (
          <Page resource="coverage_map" action="read">
            <CoverageMapPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Mapa de cobertura' },
      },

      // Admin Suggestions — Phase 02.3 P14 Task 4 (NOVO protótipo v2 UI-D-19)
      {
        path: '/admin/suggestions',
        element: (
          <Page resource="suggestion" action="read">
            <SuggestionsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Sugestões' },
      },

      // Admin Provisional Quotations — Phase 02.3 P15 (ESCR-116 + UI-D-17)
      {
        path: '/admin/provisional-quotations',
        element: (
          <Page resource="provisional_quote" action="approve">
            <ProvisionalQueuePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Cotações provisórias' },
      },

      // -----------------------------------------------------------------
      // Phase 12 Plan 16 (Sub-A2) — Admin Hinova sync UI (apólices).
      // Permission backend: `policies:admin` (read + retry). Retry sync
      // button gated to admin_alamo via UI; backend valida 401/403.
      // -----------------------------------------------------------------
      {
        path: '/admin/policies',
        element: (
          <Page resource="policies" action="read">
            <PoliciesListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Integração SGA' },
      },
      {
        path: '/admin/policies/:id',
        element: (
          <Page resource="policies" action="read">
            <PolicyDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Integração SGA', href: '/admin/policies' },
            { label: 'Detalhes' },
          ],
        },
      },

      // -----------------------------------------------------------------
      // Cotação de FROTA V1 (admin-only) — lote agregador + pagamento único.
      // Contrato: docs/contracts/ESCR-fleet-quotations.md. Permission
      // dedicada `fleet_quotations:manage` (admin_alamo + gerente).
      // -----------------------------------------------------------------
      {
        path: '/admin/fleet-quotations',
        element: (
          <Page resource="fleet_quotations" action="manage">
            <FleetQuotationsListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Cotações de frota' },
      },
      {
        path: '/admin/fleet-quotations/new',
        element: (
          <Page resource="fleet_quotations" action="manage">
            <NewFleetQuotationPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Cotações de frota', href: '/admin/fleet-quotations' },
            { label: 'Nova frota' },
          ],
        },
      },
      {
        path: '/admin/fleet-quotations/:id',
        element: (
          <Page resource="fleet_quotations" action="manage">
            <FleetQuotationDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Cotações de frota', href: '/admin/fleet-quotations' },
            { label: 'Detalhes' },
          ],
        },
      },

      // -----------------------------------------------------------------
      // Phase 12 Plan 12 — 5 admin pages (analyst queue + KPIs + war room)
      // -----------------------------------------------------------------
      {
        path: '/admin/propostas/analise',
        element: (
          <Page resource="proposals" action="analyze">
            <AnalystQueuePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Fila de análise' },
      },
      // Histórico de análises (propostas já decididas) — listagem lazy sob o
      // mesmo gate proposals:analyze. Rota estática ANTES da dinâmica :id.
      {
        path: '/admin/propostas/historico',
        element: (
          <Page resource="proposals" action="analyze">
            <AnalysisHistoryPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Fila de análise', href: '/admin/propostas/analise' },
            { label: 'Histórico' },
          ],
        },
      },
      // Troca de titularidade (WAVE 2) — bucket "Aguardando titular". Listagem
      // paginada sob o mesmo gate proposals:analyze. Rota estática ANTES da
      // dinâmica :id.
      {
        path: '/admin/propostas/troca-titularidade',
        element: (
          <Page resource="proposals" action="analyze">
            <OwnershipTransferQueuePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Fila de análise', href: '/admin/propostas/analise' },
            { label: 'Aguardando titular' },
          ],
        },
      },
      // Alias defensivo — /admin/propostas (sem /analise) é a URL natural da
      // estrutura de propostas; redireciona pra fila de análise (Kanban).
      { path: '/admin/propostas', element: <Navigate to="/admin/propostas/analise" replace /> },
      {
        path: '/admin/propostas/:id/analise',
        element: (
          <Page resource="proposals" action="analyze">
            <AnalystDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          // 3-níveis: Painel (home auto) > Fila de análise (link) > Análise
          // (página atual). Label completo "PROP-XXXX · Nome" vive no
          // PageHeader; breadcrumb fica curto pra caber em mobile.
          breadcrumb: () => [
            { label: 'Fila de análise', href: '/admin/propostas/analise' },
            { label: 'Análise' },
          ],
        },
      },
      {
        path: '/admin/notification-settings',
        element: <Navigate to="/admin/notifications/events" replace />,
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Configuração de Notificações' },
      },
      // Phase A (2026-05-22) — Gerenciar roster do time de analise.
      {
        path: '/admin/analistas',
        element: (
          <Page resource="analyst_team" action="manage">
            <AnalystTeamListPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Time de Análise' },
      },
      {
        path: '/admin/kpis',
        element: (
          <Page resource="kpis" action="read">
            <KpisDashboardPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Indicadores' },
      },
      {
        path: '/admin/sala-de-guerra',
        element: (
          <Page resource="dashboard" action="war_room">
            <WarRoomPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Sala de Guerra' },
      },

      // -----------------------------------------------------------------
      // ESCR-172.6 — Alamo Cobre (UI analista)
      // -----------------------------------------------------------------
      {
        path: '/admin/competitor-offers',
        element: (
          <Page resource="competitor-offers" action="view-queue">
            <CompetitorOffersQueuePage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Álamo Cobre' },
      },
      {
        path: '/admin/competitor-offers/:id',
        element: (
          <Page resource="competitor-offers" action="view-queue">
            <CompetitorOfferDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Álamo Cobre', href: '/admin/competitor-offers' },
            { label: 'Detalhe' },
          ],
        },
      },

      // -----------------------------------------------------------------
      // Phase 34 Plan 07 — Loja Interna admin back-office
      // -----------------------------------------------------------------
      {
        path: '/admin/store/products',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreProductsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Produtos' },
      },
      {
        path: '/admin/store/products/new',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreProductFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Loja — Produtos', href: '/admin/store/products' },
            { label: 'Novo produto' },
          ],
        },
      },
      {
        path: '/admin/store/products/:id/edit',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreProductFormPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Loja — Produtos', href: '/admin/store/products' },
            { label: 'Editar produto' },
          ],
        },
      },
      {
        path: '/admin/store/stock',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreStockPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Estoque' },
      },
      {
        // ESCR-store-vouchers — vouchers de desconto da loja
        path: '/admin/store/vouchers',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreVouchersPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Vouchers' },
      },
      {
        path: '/admin/store/orders',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreOrdersPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Pedidos' },
      },
      {
        path: '/admin/store/orders/:id',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreOrderDetailPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: {
          breadcrumb: () => [
            { label: 'Loja — Pedidos', href: '/admin/store/orders' },
            { label: 'Detalhe do pedido' },
          ],
        },
      },
      {
        // ESCR-250.4 — config de crédito do consultor (sob "Loja Interna")
        path: '/admin/store/credit',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreCreditPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Crédito do consultor' },
      },
      {
        // ESCR-251.7 — acompanhamento de parcelas/descontos (compra → parcela → comissão)
        path: '/admin/store/credit/installments',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreInstallmentsPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Parcelas e descontos' },
      },
      {
        // ESCR-251.8 — relatório financeiro do crédito (contratado/cobrado/a-receber)
        path: '/admin/store/credit/financial',
        element: (
          <Page resource="ecommerce" action="manage">
            <AdminStoreFinancialReportPage />
          </Page>
        ),
        errorElement: <AdminErrorBoundary />,
        handle: { breadcrumb: 'Loja — Relatório financeiro' },
      },

      // Redirects: PT admin paths → EN canonical
      { path: '/admin/notificacoes', element: <Navigate to="/admin/notifications" replace /> },
      { path: '/admin/multinivel', element: <Navigate to="/admin/multilevel" replace /> },
    ],
  },

  // Catch-all
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
]);
